import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../../../services/api';
import type { Chat, Reminder } from '../../../types';

type Props = {
  chats: Chat[];
};

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  return new Intl.DateTimeFormat('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const reminderStatusLabel: Record<Reminder['status'], string> = {
  pending: 'Wachtend',
  fired: 'Afgegaan',
  cancelled: 'Geannuleerd',
};

const RemindersPanel = ({ chats }: Props) => {
  const [items, setItems] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [chatId, setChatId] = useState('');
  const [title, setTitle] = useState('');
  const [remindAt, setRemindAt] = useState('');
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef(false);
  const [minDateTime] = useState(() =>
    new Date(Date.now() + 60_000).toISOString().slice(0, 16),
  );

  const load = useCallback(async () => {
    try {
      const data = await api.reminders();
      setItems(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!chatId || !title.trim() || !remindAt) return;
    setSaving(true);
    try {
      const iso = new Date(remindAt).toISOString();
      const created = await api.createReminder({
        chatId,
        title: title.trim(),
        remindAt: iso,
      });
      setItems((current) =>
        [...current, created].sort((a, b) => Date.parse(a.remindAt) - Date.parse(b.remindAt)),
      );
      setTitle('');
      setRemindAt('');
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      const cancelled = await api.cancelReminder(id);
      setItems((current) => current.map((item) => (item.id === id ? cancelled : item)));
    } catch {
      // ignore
    }
  };

  const chatTitle = (id: string): string =>
    chats.find((c) => c.id === id)?.title ?? id.slice(0, 8);

  return (
    <div className="reminders-panel">
      <p className="tool-description">
        Stel herinneringen in voor opvolging. De scheduler controleert elke 30 seconden of er
        herinneringen due zijn.
      </p>
      <div className="reminder-form">
        <label className="tool-field">
          <span>Chat</span>
          <select value={chatId} onChange={(event) => setChatId(event.target.value)}>
            <option value="">Selecteer een chat…</option>
            {chats.map((chat) => (
              <option key={chat.id} value={chat.id}>
                {chat.title}
              </option>
            ))}
          </select>
        </label>
        <label className="tool-field">
          <span>Titel</span>
          <input
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Klant terugbellen"
            maxLength={200}
          />
        </label>
        <label className="tool-field">
          <span>Herinneren op</span>
          <input
            type="datetime-local"
            value={remindAt}
            onChange={(event) => setRemindAt(event.target.value)}
            min={minDateTime}
          />
        </label>
        <button
          type="button"
          className="button primary small"
          disabled={saving || !chatId || !title.trim() || !remindAt}
          onClick={handleCreate}
        >
          {saving ? 'Instellen…' : 'Herinnering instellen'}
        </button>
      </div>
      <div className="reminder-list">
        <h4>Herinneringen</h4>
        {loading && <p className="muted small">Laden…</p>}
        {!loading && items.length === 0 && (
          <p className="empty-state small">Geen herinneringen.</p>
        )}
        {items.map((item) => (
          <div key={item.id} className={`reminder-item ${item.status}`}>
            <div className="reminder-item-header">
              <strong>{item.title}</strong>
              <span className={`reminder-status ${item.status}`}>
                {reminderStatusLabel[item.status]}
              </span>
            </div>
            <div className="reminder-item-meta">
              <span>💬 {chatTitle(item.chatId)}</span>
              <span>🔔 {formatDateTime(item.remindAt)}</span>
              {item.status === 'pending' && (
                <button
                  type="button"
                  className="button danger small"
                  onClick={() => void handleCancel(item.id)}
                >
                  Annuleren
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RemindersPanel;
